import { useState, useEffect } from 'react';
import MapContainer from '@/components/MapContainer';
import Header from '@/components/Header';
import EventInfoPanel from '@/components/EventInfoPanel';
import Onboarding from '@/components/Onboarding';
import SearchResultsList from '@/components/SearchResultsList';
import ListEventDialog from '@/components/ListEventDialog';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Map, List, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get the next occurrence date for an event (handles recurring events)
const getEventSortDate = (event) => {
  if (event.isRecurring && event.recurringDays && event.recurringDays.length > 0) {
    // For recurring events, find the next occurrence
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const todayDay = today.getDay();
    
    // Find the next occurrence within the next 7 days
    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() + i);
      const checkDayName = dayNames[checkDate.getDay()];
      
      if (event.recurringDays.includes(checkDayName)) {
        return checkDate;
      }
    }
    
    // Fallback: if no occurrence in next 7 days, return today + 7 days
    const fallbackDate = new Date(today);
    fallbackDate.setDate(fallbackDate.getDate() + 7);
    return fallbackDate;
  } else {
    // For non-recurring events, use the event date
    return new Date(event.eventDate);
  }
};

export default function MapView() {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [hasOnboarded, setHasOnboarded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userLocation, setUserLocation] = useState(null); // For map centering (can be city or GPS)
  const [actualGPSLocation, setActualGPSLocation] = useState(null); // Always stores actual GPS location
  const [isActualUserLocation, setIsActualUserLocation] = useState(false); // Track if location is from GPS
  const [allEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [dateFilter, setDateFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(null); // { l1: 'sports', l2: 'yoga' }
  const [showListView, setShowListView] = useState(false);
  const [showListEventDialog, setShowListEventDialog] = useState(false);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [markerPosition, setMarkerPosition] = useState(null);
  const [isInBangalore, setIsInBangalore] = useState(true);
  const [selectedCity, setSelectedCity] = useState('Bangalore');

  // Check if coordinates are in Bangalore (rough bounding box)
  const checkIfInBangalore = (lat, lng) => {
    const bangaloreBounds = {
      north: 13.2,
      south: 12.7,
      east: 77.9,
      west: 77.4
    };
    return lat >= bangaloreBounds.south && lat <= bangaloreBounds.north &&
           lng >= bangaloreBounds.west && lng <= bangaloreBounds.east;
  };

  useEffect(() => {
    // Check if user has already onboarded
    const userData = localStorage.getItem('localvibe_user');
    if (userData) {
      setHasOnboarded(true);
      // Request location permission
      requestLocationPermission();
    }
    // Fetch events from API (expiry check happens here)
    fetchEvents();
    // Log user visit (once per day)
    logVisit();
    setIsLoading(false);
    
    // Global function for recenter to update location
    window.updateUserLocation = (newLocation) => {
      setUserLocation(newLocation);
      setIsActualUserLocation(false); // Programmatic update, not GPS
      toast.success('Location updated');
    };
    
    return () => {
      delete window.updateUserLocation;
    };
  }, []);

  // Handle shared event link - check URL parameters
  useEffect(() => {
    if (allEvents.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const eventId = urlParams.get('event');
      
      if (eventId) {
        // Find the event with this ID - check both _id and id fields
        const event = allEvents.find(e => e._id === eventId || e.id === eventId);
        if (event) {
          // Open the event panel
          setSelectedEvent(event);
          // Center map on the event
          if (event.position) {
            setUserLocation({ lat: event.position.lat, lng: event.position.lng });
            setIsActualUserLocation(false); // Centering on event, not GPS location
          }
          toast.success('Opening shared event...');
          
          // Clear the URL parameter after opening (keeps URL clean)
          window.history.replaceState({}, '', window.location.pathname);
        } else {
          toast.error('Event not found');
          console.error('Event ID not found:', eventId, 'Available events:', allEvents.length);
        }
      }
    }
  }, [allEvents]);

  useEffect(() => {
    // Filter events based on search query, date filter, and category filter
    let filtered = allEvents;

    // Apply search filter - includes all event info panel fields
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event => 
        event.title?.toLowerCase().includes(query) ||
        event.category?.toLowerCase().includes(query) ||
        event.l1_category?.toLowerCase().includes(query) ||
        event.l2_category?.toLowerCase().includes(query) ||
        event.location?.toLowerCase().includes(query) ||
        event.description?.toLowerCase().includes(query) ||
        event.city?.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(event => {
        if (categoryFilter.l2) {
          // Filter by L2 category
          return event.l2_category === categoryFilter.l2;
        } else if (categoryFilter.l1) {
          // Filter by L1 category
          return event.l1_category === categoryFilter.l1;
        }
        return true;
      });
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      console.log('🔍 Applying date filter:', dateFilter);
      const beforeCount = filtered.length;
      
      filtered = filtered.filter(event => {
        try {
          // Handle recurring events
          if (event.isRecurring && event.recurringDays && event.recurringDays.length > 0) {
            let targetDate;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);

            if (dateFilter === 'today') {
              targetDate = today;
            } else if (dateFilter === 'tomorrow') {
              targetDate = tomorrow;
            } else {
              // Custom date filter - dateFilter is YYYY-MM-DD format
              targetDate = new Date(dateFilter);
            }

            // Get day name from date
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const targetDayName = dayNames[targetDate.getDay()];

            // Check if event occurs on this day
            return event.recurringDays.includes(targetDayName);
          }

          // Handle non-recurring events
          // Normalize date to YYYY-MM-DD format for comparison
          const normalizeDate = (dateStr) => {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return null;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };
          
          const eventDateNormalized = normalizeDate(event.eventDate);
          if (!eventDateNormalized) return false;
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);

          if (dateFilter === 'today') {
            const todayNormalized = normalizeDate(today);
            return eventDateNormalized === todayNormalized;
          } else if (dateFilter === 'tomorrow') {
            const tomorrowNormalized = normalizeDate(tomorrow);
            return eventDateNormalized === tomorrowNormalized;
          } else {
            // Custom date filter - dateFilter is YYYY-MM-DD format
            // Compare normalized dates
            return eventDateNormalized === dateFilter;
          }
        } catch (error) {
          console.log('❌ Error filtering event:', event.title, error);
          return false;
        }
      });
      
      console.log(`✅ Date filter results: ${beforeCount} → ${filtered.length} events`);
    }

    // Add distance calculation for all events ONLY if user has given actual GPS location
    if (actualGPSLocation) {
      filtered = filtered.map(event => ({
        ...event,
        distance: calculateDistance(
          actualGPSLocation.lat,
          actualGPSLocation.lng,
          event.position.lat,
          event.position.lng
        )
      }));
    }

    // Sort by date first, then by start time, then by distance (for list view)
    if (viewMode === 'list') {
      filtered = filtered.sort((a, b) => {
        // Primary sort: by next occurrence date (earliest first) - handles recurring events properly
        const dateA = getEventSortDate(a);
        const dateB = getEventSortDate(b);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }
        
        // Secondary sort: by start time (earliest first)
        if (a.startTime && b.startTime) {
          const timeA = a.startTime.split(':').map(n => parseInt(n, 10));
          const timeB = b.startTime.split(':').map(n => parseInt(n, 10));
          const minutesA = timeA[0] * 60 + timeA[1];
          const minutesB = timeB[0] * 60 + timeB[1];
          if (minutesA !== minutesB) {
            return minutesA - minutesB;
          }
        }
        
        // Tertiary sort: by distance (closest first)
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        return 0;
      });
    } else if (searchQuery.trim() && actualGPSLocation) {
      // For search in map view, sort by distance -> date -> start time
      filtered = filtered.sort((a, b) => {
        // Primary: distance (closest first)
        const distanceCompare = (a.distance || Infinity) - (b.distance || Infinity);
        if (distanceCompare !== 0) {
          return distanceCompare;
        }
        
        // Secondary: date (earliest first)
        const dateA = getEventSortDate(a);
        const dateB = getEventSortDate(b);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }
        
        // Tertiary: start time (earliest first)
        if (a.startTime && b.startTime) {
          const timeA = a.startTime.split(':').map(n => parseInt(n, 10));
          const timeB = b.startTime.split(':').map(n => parseInt(n, 10));
          const minutesA = timeA[0] * 60 + timeA[1];
          const minutesB = timeB[0] * 60 + timeB[1];
          return minutesA - minutesB;
        }
        
        return 0;
      });
    }

    setFilteredEvents(filtered);
  }, [searchQuery, allEvents, dateFilter, categoryFilter, userLocation, viewMode]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events`);
      const data = await response.json();
      
      // Filter out expired events
      const activeEvents = data.filter(event => !isEventExpired(event));
      
      console.log(`📊 Events: ${data.length} total, ${activeEvents.length} active, ${data.length - activeEvents.length} expired`);
      
      setAllEvents(activeEvents);
      setFilteredEvents(activeEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  };

  const logVisit = async () => {
    try {
      const userData = localStorage.getItem('localvibe_user');
      let visitData = {
        userName: null,
        userEmail: null,
        userPhone: null
      };

      if (userData) {
        const user = JSON.parse(userData);
        visitData = {
          userName: user.name,
          userEmail: user.email,
          userPhone: user.phone
        };
      }

      await fetch(`${BACKEND_URL}/api/activity/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(visitData)
      });
    } catch (error) {
      console.error('Error logging visit:', error);
      // Silently fail - don't show error to user
    }
  };

  const isEventExpired = (event) => {
    try {
      // Recurring events never expire (they repeat weekly)
      if (event.isRecurring) {
        return false;
      }
      
      // Use structured date/time format
      if (!event.eventDate || !event.startTime) {
        return false; // Can't parse, assume not expired
      }

      // Get current time in IST (UTC+5:30)
      const now = new Date();
      const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
      const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
      const nowISTTime = utcTime + istOffset;
      
      // Parse event date - handles both "YYYY-MM-DD" and "M/D/YYYY" formats
      const eventDate = new Date(event.eventDate);
      
      // Parse start time - handles both "HH:MM" and "H:MM" formats
      const timeParts = event.startTime.split(':');
      if (timeParts.length !== 2) {
        return false; // Invalid time format
      }
      
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      
      // Validate parsed values
      if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return false; // Invalid time values
      }
      
      // Create event timestamp in IST
      // Event times are stored in IST, so we treat the date/time values as IST
      const eventYear = eventDate.getFullYear();
      const eventMonth = eventDate.getMonth();
      const eventDay = eventDate.getDate();
      
      // Create UTC timestamp representing this IST date/time
      const eventISTTime = Date.UTC(eventYear, eventMonth, eventDay, hours, minutes, 0, 0);

      // Compare: event is expired if start time has passed in IST
      return eventISTTime < nowISTTime;
    } catch (error) {
      console.error('Error checking event expiry:', error);
      return false; // On error, don't filter out
    }
  };

  const requestLocationPermission = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const gpsLocation = { lat, lng };
          
          // Store actual GPS location permanently
          setActualGPSLocation(gpsLocation);
          setUserLocation(gpsLocation);
          setIsActualUserLocation(true); // This is actual GPS location
          
          // Check if user is in Bangalore
          const inBangalore = checkIfInBangalore(lat, lng);
          setIsInBangalore(inBangalore);
        },
        (error) => {
          console.log('Location permission denied:', error);
          setIsActualUserLocation(false);
          setActualGPSLocation(null);
        }
      );
    }
  };

  const handleOnboardingComplete = () => {
    setHasOnboarded(true);
    requestLocationPermission();
    // Log the user's first visit
    logVisit();
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      setShowListView(true);
    }
  };

  const handleBackToMap = () => {
    setShowListView(false);
    setSearchQuery('');
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'map') {
      setShowListView(false);
    }
  };

  const handleLocationSearch = (location) => {
    // Update map center to the selected city location
    // BUT keep actualGPSLocation intact for distance calculations
    setUserLocation({ lat: location.lat, lng: location.lng });
    setSelectedCity(location.name);
    
    // Check if selected location is in Bangalore
    const inBangalore = checkIfInBangalore(location.lat, location.lng);
    setIsInBangalore(inBangalore);
    
    if (inBangalore) {
      toast.success(`Searching events in ${location.name}`);
    }
    
    // Note: actualGPSLocation remains unchanged - distance always from GPS if available
  };

  const handleRecenterCallback = (newLocation, isGPS = false) => {
    // Update map center location
    setUserLocation(newLocation);
    
    // If it's actual GPS, also update the GPS location store
    if (isGPS) {
      setActualGPSLocation(newLocation);
      setIsActualUserLocation(true);
    }
    
    // Check if the new location is in Bangalore
    const inBangalore = checkIfInBangalore(newLocation.lat, newLocation.lng);
    setIsInBangalore(inBangalore);
    
    // If back in Bangalore, update city name
    if (inBangalore) {
      setSelectedCity('Bangalore');
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center gradient-hero">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground mt-4">Loading Tandem...</p>
        </div>
      </div>
    );
  }

  if (!hasOnboarded) {
    return (
      <>
        <Onboarding onComplete={handleOnboardingComplete} />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden">
      <Header 
        searchQuery={searchQuery} 
        onSearchChange={setSearchQuery}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        categoryFilter={categoryFilter}
        onCategoryFilterChange={setCategoryFilter}
        allEvents={allEvents}
        onSearchSubmit={handleSearchSubmit}
        onListEvent={() => setShowListEventDialog(true)}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onLocationSearch={handleLocationSearch}
      />
      <div className="flex-1 relative pt-12 md:pt-0 pb-16 md:pb-0 overflow-hidden">
        {/* Coming Soon Overlay for non-Bangalore - Positioned below header */}
        {!isInBangalore && (
          <div className="absolute top-0 left-0 right-0 bottom-0 md:bottom-0 z-40 flex items-center justify-center bg-background/95 backdrop-blur-sm">
            <div className="text-center px-8 max-w-md">
              <div className="mb-6">
                <div className="text-6xl mb-4 animate-bounce">🚀</div>
                <h2 className="text-3xl font-bold text-foreground mb-3">Coming to Your City Soon!</h2>
                <p className="text-lg text-muted-foreground mb-2">
                  Tandem is currently available in <span className="font-semibold text-primary">Bangalore</span> only.
                </p>
                <p className="text-sm text-muted-foreground">
                  We're working hard to bring amazing local experiences to {selectedCity} and other cities soon!
                </p>
              </div>
            </div>
          </div>
        )}
        
        {(showListView || viewMode === 'list') ? (
          <SearchResultsList 
            events={filteredEvents}
            searchQuery={searchQuery}
            userLocation={actualGPSLocation}
            isActualUserLocation={!!actualGPSLocation}
            onEventSelect={setSelectedEvent}
            onBackToMap={handleBackToMap}
            showBackButton={showListView && viewMode === 'map'}
            onRequestLocation={requestLocationPermission}
          />
        ) : (
          <MapContainer 
            selectedEvent={selectedEvent}
            onEventSelect={setSelectedEvent}
            filteredEvents={filteredEvents}
            searchQuery={searchQuery}
            userLocation={userLocation}
            isActualUserLocation={isActualUserLocation}
            onMarkerPosition={setMarkerPosition}
            onRecenter={handleRecenterCallback}
          />
        )}
        {selectedEvent && !isInBangalore && null /* Hide event panel when overlay is shown */}
        {selectedEvent && isInBangalore && (
          <EventInfoPanel 
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            userLocation={userLocation}
            markerPosition={markerPosition}
          />
        )}
      </div>
      
      {/* Mobile & iPad Bottom Navigation - Airbnb-inspired */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-50 safe-area-pb">
        <div className="flex items-center justify-around h-16 px-4">
          <button
            onClick={() => handleViewModeChange('map')}
            className={`flex flex-col items-center justify-center flex-1 py-2 ${
              viewMode === 'map' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Map className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Map</span>
          </button>
          
          <button
            onClick={() => handleViewModeChange('list')}
            className={`flex flex-col items-center justify-center flex-1 py-2 ${
              viewMode === 'list' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <List className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Events List</span>
          </button>
          
          <button
            onClick={() => setShowListEventDialog(true)}
            className="flex flex-col items-center justify-center flex-1 py-2 text-primary"
          >
            <Plus className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Add Event</span>
          </button>
        </div>
      </div>
      
      <ListEventDialog 
        open={showListEventDialog}
        onOpenChange={setShowListEventDialog}
      />
      <Toaster position="top-center" richColors />
    </div>
  );
}
